/**
 * Typed API helpers for parkify-sales. The renderer payload schema is the
 * source of truth — see parkify-sales/app/services/proposal/models.py.
 */

export interface ProposalPayload {
  prospect: string
  company: string
  city: string
  rep: string
  email: string
  date: string
  // Everything else has server-side defaults; we only let the rep
  // override the cover + a couple of pricing knobs from the portal for
  // now. The full schema is still accepted by the renderer.
  pricing?: {
    callout?: { headline: string; detail: string } | null
    recommended_plan?: 'single' | 'portfolio'
  }
  notes?: string
}

export interface ProposalRecord {
  id: string
  rep_email: string
  rep_name?: string
  prospect: string
  company: string
  city: string
  // `approved` is the new gate for the Send Contract flow — proposals
  // in this state expose the Send Contract surface in the portal.
  status: 'draft' | 'generated' | 'sent' | 'approved' | 'won' | 'lost'
  call_notes?: string
  payload: ProposalPayload
  pdf_gcs_path?: string
  created_at: string
  updated_at: string
}

export type ExtractProvider = 'claude' | 'gemini'

export type ExtractMode = 'extract' | 'draft' | 'surprise' | 'canvas'

export interface ExtractResponse {
  payload: ProposalPayload
  confidence: Record<string, 'high' | 'medium' | 'low'>
  warnings: string[]
  provider?: ExtractProvider
  model?: string
  calendly_enriched?: boolean
  /** True when the response came from /proposal/draft (full-AI mode). */
  ai_drafted?: boolean
  /** True when the response came from /proposal/surprise (creative mode). */
  ai_surprise?: boolean
  /** The prospect email the backend actually used (body value first, then
   *  auto-detected from notes). Surfaces to the rep on the review screen. */
  resolved_prospect_email?: string
  /** True when `resolved_prospect_email` came from the auto-detect path
   *  rather than the rep typing it explicitly. */
  prospect_email_autodetected?: boolean
}

function asJsonError(text: string, status: number): Error {
  try {
    const parsed = JSON.parse(text) as { detail?: string; error?: string }
    return new Error(parsed.detail ?? parsed.error ?? `HTTP ${status}`)
  } catch {
    return new Error(text || `HTTP ${status}`)
  }
}

/** Canvas-mode result. `/proposal/canvas` returns a JSON envelope so the
 *  portal has both the rendered PDF (for preview) and the source HTML
 *  (for the drag-to-nudge Edit Layout view). */
export interface CanvasResponse {
  /** Full HTML document the LLM generated — brand CSS already injected.
   *  Drop into a sandboxed iframe via `srcdoc` for the editor. */
  html: string
  /** The rendered PDF, ready for `URL.createObjectURL`. Decoded from
   *  the base64 envelope on our side so callers get a Blob. */
  pdf: Blob
  /** One-line creative-direction summary the LLM optionally emits. */
  summary?: string
  provider?: ExtractProvider
  model?: string
  calendly_enriched: boolean
  /** Email the backend resolved (body value first, then auto-detect). */
  resolved_prospect_email?: string
  /** True when the resolved email came from the auto-detect path. */
  prospect_email_autodetected?: boolean
}

/** Decode a base64-encoded PDF string into a Blob the browser can
 *  hand to `URL.createObjectURL`. Pure-browser path — no Buffer needed. */
function pdfBlobFromBase64(b64: string): Blob {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: 'application/pdf' })
}

export async function canvasProposal(
  apiBase: string,
  headers: HeadersInit,
  notes: string,
  repName: string,
  repEmail: string,
  provider: ExtractProvider,
  model?: string,
  prospectEmail?: string,
): Promise<CanvasResponse> {
  const res = await fetch(`${apiBase}/proposal/canvas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({
      notes,
      rep_name: repName,
      rep_email: repEmail,
      provider,
      ...(model ? { model } : {}),
      ...(prospectEmail ? { prospect_email: prospectEmail } : {}),
    }),
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  const body = (await res.json()) as {
    html: string
    pdf_base64: string
    summary?: string | null
    provider?: ExtractProvider | null
    model?: string | null
    calendly_enriched: boolean
    resolved_prospect_email?: string | null
    prospect_email_autodetected: boolean
  }
  return {
    html: body.html,
    pdf: pdfBlobFromBase64(body.pdf_base64),
    summary: body.summary || undefined,
    provider: (body.provider as ExtractProvider | null) || undefined,
    model: body.model || undefined,
    calendly_enriched: body.calendly_enriched,
    resolved_prospect_email: body.resolved_prospect_email || undefined,
    prospect_email_autodetected: body.prospect_email_autodetected,
  }
}

/** Rerender an edited Canvas HTML document to PDF. Used by the Edit
 *  Layout view after the rep drags boxes around — no LLM call,
 *  WeasyPrint-only path. */
export async function rerenderCanvas(
  apiBase: string,
  headers: HeadersInit,
  html: string,
): Promise<Blob> {
  const res = await fetch(`${apiBase}/proposal/canvas/render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ html }),
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return await res.blob()
}

export async function extractProposal(
  apiBase: string,
  headers: HeadersInit,
  notes: string,
  repName: string,
  repEmail: string,
  provider: ExtractProvider,
  model?: string,
  prospectEmail?: string,
  mode: ExtractMode = 'extract',
): Promise<ExtractResponse> {
  // Canvas mode goes through canvasProposal() — it returns a PDF blob, not
  // an ExtractResponse. Call sites must dispatch based on mode before
  // reaching here. Defend with an explicit error so the failure is clear.
  if (mode === 'canvas') {
    throw new Error(
      'Use canvasProposal() for canvas mode — it returns a PDF blob, not JSON.',
    )
  }
  const path =
    mode === 'surprise'
      ? '/proposal/surprise'
      : mode === 'draft'
        ? '/proposal/draft'
        : '/proposal/extract'
  const res = await fetch(`${apiBase}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({
      notes,
      rep_name: repName,
      rep_email: repEmail,
      provider,
      ...(model ? { model } : {}),
      ...(prospectEmail ? { prospect_email: prospectEmail } : {}),
    }),
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as ExtractResponse
}

export async function listProposals(
  apiBase: string,
  headers: HeadersInit,
  opts: { mine?: boolean } = {},
): Promise<ProposalRecord[]> {
  const params = new URLSearchParams()
  if (opts.mine) params.set('mine', 'true')
  const res = await fetch(`${apiBase}/proposal/list?${params.toString()}`, {
    headers,
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  const body = (await res.json()) as { proposals: ProposalRecord[] }
  return body.proposals
}

export async function getProposal(
  apiBase: string,
  headers: HeadersInit,
  id: string,
): Promise<ProposalRecord> {
  const res = await fetch(`${apiBase}/proposal/${encodeURIComponent(id)}`, {
    headers,
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as ProposalRecord
}

export async function previewProposal(
  apiBase: string,
  headers: HeadersInit,
  payload: ProposalPayload,
): Promise<Blob> {
  const res = await fetch(`${apiBase}/proposal/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ payload }),
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return await res.blob()
}

export async function saveProposal(
  apiBase: string,
  headers: HeadersInit,
  payload: ProposalPayload,
  callNotes: string | undefined,
): Promise<ProposalRecord> {
  const res = await fetch(`${apiBase}/proposal/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ payload, call_notes: callNotes }),
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as ProposalRecord
}

export async function updateProposal(
  apiBase: string,
  headers: HeadersInit,
  id: string,
  patch: Partial<Pick<ProposalRecord, 'status' | 'call_notes'>> & {
    payload?: ProposalPayload
  },
): Promise<ProposalRecord> {
  const res = await fetch(`${apiBase}/proposal/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(patch),
  })
  if (!res.ok) throw asJsonError(await res.text().catch(() => ''), res.status)
  return (await res.json()) as ProposalRecord
}

export function proposalPdfUrl(apiBase: string, id: string, token: string): string {
  // We pass the JWT via the access_token query param because <a download>
  // can't set Authorization headers. The backend accepts both.
  const u = new URL(`${apiBase}/proposal/${encodeURIComponent(id)}/pdf`)
  u.searchParams.set('access_token', token)
  return u.toString()
}
